'use client'

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react'
import { hasVideoFiles } from '@/lib/uploadVideo'
import { hasAudioFiles } from '@/lib/uploadAudio'
import { hasImageFiles } from '@/lib/uploadImage'

export type DragMediaType = 'video' | 'audio' | 'image' | null

interface FileDragContextValue {
  /** True when a media file is being dragged over the window */
  isFileDragActive: boolean
  /** The type of media being dragged */
  dragMediaType: DragMediaType
  /** Increments once per drag session. Consumers that track "the drag is over
      ME" should store this id (not a boolean) — a drag can end without any
      event reaching them, and stale state from session N can never match
      session N+1. */
  dragId: number
}

const FileDragContext = createContext<FileDragContextValue>({
  isFileDragActive: false,
  dragMediaType: null,
  dragId: 0,
})

export function useFileDrag() {
  return useContext(FileDragContext)
}

interface FileDragProviderProps {
  children: React.ReactNode
}

export function FileDragProvider({ children }: FileDragProviderProps) {
  const [isFileDragActive, setIsFileDragActive] = useState(false)
  const [dragMediaType, setDragMediaType] = useState<DragMediaType>(null)
  const [dragId, setDragId] = useState(0)
  const dragCounterRef = useRef(0)
  // Dragging an in-app <img> makes Chrome report Files + an image MIME —
  // indistinguishable from an OS file drag by dataTransfer alone. The only
  // reliable tell: OS drags never fire dragstart inside this document.
  const internalDragRef = useRef(false)

  const resetDragState = useCallback(() => {
    dragCounterRef.current = 0
    setIsFileDragActive(false)
    setDragMediaType(null)
  }, [])

  const handleDragStart = useCallback(() => {
    internalDragRef.current = true
  }, [])

  const handleDragEnter = useCallback((e: DragEvent) => {
    if (!e.dataTransfer) return
    if (internalDragRef.current) return

    // Detect media type
    const isVideo = hasVideoFiles(e.dataTransfer)
    const isAudio = hasAudioFiles(e.dataTransfer)
    const isImage = hasImageFiles(e.dataTransfer)

    if (isVideo || isAudio || isImage) {
      dragCounterRef.current++
      if (dragCounterRef.current === 1) {
        setIsFileDragActive(true)
        setDragId(n => n + 1)
        // Priority: video > audio > image (video files may also match audio)
        if (isVideo) {
          setDragMediaType('video')
        } else if (isAudio) {
          setDragMediaType('audio')
        } else {
          setDragMediaType('image')
        }
      }
    }
  }, [])

  const handleDragLeave = useCallback(
    (e: DragEvent) => {
      // Check if we're leaving the window entirely
      // relatedTarget is null when leaving the window
      if (e.relatedTarget === null) {
        resetDragState()
        return
      }

      if (dragCounterRef.current > 0) {
        dragCounterRef.current--
        if (dragCounterRef.current === 0) {
          resetDragState()
        }
      }
    },
    [resetDragState],
  )

  const handleDragOver = useCallback((e: DragEvent) => {
    if (!e.dataTransfer) return
    if (internalDragRef.current) return
    // Need to prevent default to allow drop
    if (hasVideoFiles(e.dataTransfer) || hasAudioFiles(e.dataTransfer) || hasImageFiles(e.dataTransfer)) {
      e.preventDefault()
    }
  }, [])

  const handleDrop = useCallback(() => {
    // Internal flag clears only when the drag truly ends (drop/dragend) — a
    // window-exit dragleave mid-internal-drag must not re-arm file handling.
    internalDragRef.current = false
    resetDragState()
  }, [resetDragState])

  // Also reset on dragend (fires when drag is cancelled, e.g., pressing Escape)
  const handleDragEnd = useCallback(() => {
    internalDragRef.current = false
    resetDragState()
  }, [resetDragState])

  // Reset when window loses focus (e.g., dragging to another app)
  const handleWindowBlur = useCallback(() => {
    resetDragState()
  }, [resetDragState])

  useEffect(() => {
    // Capture phase so a source element's stopPropagation can't hide the drag start.
    window.addEventListener('dragstart', handleDragStart, true)
    window.addEventListener('dragenter', handleDragEnter)
    window.addEventListener('dragleave', handleDragLeave)
    window.addEventListener('dragover', handleDragOver)
    // Use capture phase so we reset state before any element handlers can stopPropagation
    window.addEventListener('drop', handleDrop, true)
    window.addEventListener('dragend', handleDragEnd, true)
    window.addEventListener('blur', handleWindowBlur)

    return () => {
      window.removeEventListener('dragstart', handleDragStart, true)
      window.removeEventListener('dragenter', handleDragEnter)
      window.removeEventListener('dragleave', handleDragLeave)
      window.removeEventListener('dragover', handleDragOver)
      window.removeEventListener('drop', handleDrop, true)
      window.removeEventListener('dragend', handleDragEnd, true)
      window.removeEventListener('blur', handleWindowBlur)
    }
  }, [handleDragStart, handleDragEnter, handleDragLeave, handleDragOver, handleDrop, handleDragEnd, handleWindowBlur])

  return (
    <FileDragContext.Provider value={{ isFileDragActive, dragMediaType, dragId }}>{children}</FileDragContext.Provider>
  )
}
